<template>
  <RevDrawer
    :back-button-label="i18n(modalBuybackTranslations.backButtonLabel)"
    :close-button-label="i18n(modalBuybackTranslations.close)"
    :has-back-button="!showIntro"
    :name="modalName"
    :title="i18n(translations.modalTitle, { orderId })"
    @back="goToPreviousStep"
    @close="setIntroStep"
  >
    <template #body>
      <IntroView v-if="showIntro" :actions @next="nextStep" />

      <CounterOfferReasonsView
        v-if="showCounterOfferReasons"
        :reasons-groups="counterOfferReasonsGroups"
        @next="nextStep"
      />

      <CounterOfferDetailsView
        v-if="showCounterOfferDetails"
        :offer-price
        :reasons-groups="counterOfferReasonsGroups"
        @submit="setCounterOffer"
      />

      <SuspendReasonsView
        v-if="showSuspendReasons"
        :reasons-group="activeSuspendReasons"
        :step="activeStep"
        @submit="handleSubmitSuspend"
      />
    </template>
    <template #footer>
      <RevButton
        data-qa="next-button"
        :form="activeStep"
        full-width="always"
        :loading="isLoading"
        type="submit"
        variant="primary"
      >
        {{ buttonLabel }}
      </RevButton>
    </template>
  </RevDrawer>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'

import type { HttpApiError } from '@backmarket/http-api'
import {
  getRefurbisherOrderCounterOffersReasons,
  getRefurbisherSuspendReasons,
  putRefurbisherOrderCounterOffer,
  putRefurbisherOrderSuspension,
} from '@backmarket/http-api/src/api-specs-buyback/seller/refurbisher'
import type {
  BuybackOrderDetailsOnHoldAction,
  BuybackOrderDetailsSuspensionReasons,
} from '@backmarket/http-api/src/api-specs-buyback/seller/refurbisher.types'
import modalBuybackTranslations from '@backmarket/nuxt-layer-buyback/utils/Modal.translations'
import MODAL_NAMES from '@backmarket/nuxt-layer-buyback/utils/modals'
import { $httpFetch } from '@backmarket/nuxt-module-http/$httpFetch'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useLogger } from '@backmarket/nuxt-module-logger/useLogger'
import { useTheToast } from '@backmarket/nuxt-module-toast/useTheToast'
import { isEmpty } from '@backmarket/utils/object/isEmpty'
import { RevButton } from '@ds/components/Button'
import { RevDrawer } from '@ds/components/Drawer'
import { closeModal } from '@ds/components/ModalBase'

import type { CounterOfferFormattedReasonsGroup } from '~/scopes/buyback/pages/orderDetails/components/OrderOnHoldModal/counterOfferReasonsGroupsAdapter'

import translations from './OrderOnHoldModal.translations'
import CounterOfferDetailsView from './components/CounterOfferDetailsView/CounterOfferDetailsView.vue'
import CounterOfferReasonsView from './components/CounterOfferReasonsView/CounterOfferReasonsView.vue'
import IntroView from './components/IntroView/IntroView.vue'
import SuspendReasonsView from './components/SuspendReasonsView/SuspendReasonsView.vue'
import { ON_HOLD_STEPS } from './constants'
import { counterOfferReasonsGroupAdapter } from './counterOfferReasonsGroupsAdapter'

const props = defineProps<{
  orderId: string
  offerPrice: string
  actions: BuybackOrderDetailsOnHoldAction[]
}>()

const emit = defineEmits<{
  (e: 'success', payload: { message: string; title: string }): void
  (e: 'error'): void
}>()

const { openErrorToast } = useTheToast()
const i18n = useI18n()
const logger = useLogger()
const activeStep = ref(ON_HOLD_STEPS.INTRO)
const activeSuspendReasons = ref<
  BuybackOrderDetailsSuspensionReasons | undefined
>()
const isLoading = ref(false)
const counterOfferReasonsGroups = ref<CounterOfferFormattedReasonsGroup[]>([])
const suspendReasons = ref<
  Record<string, BuybackOrderDetailsSuspensionReasons>
>({})

const modalName = computed(() => MODAL_NAMES.REFURBISHER_ORDER_ON_HOLD)
const showIntro = computed(() => activeStep.value === ON_HOLD_STEPS.INTRO)
const showCounterOfferReasons = computed(
  () => activeStep.value === ON_HOLD_STEPS.MAKE_COUNTER_OFFER_REASONS,
)
const showCounterOfferDetails = computed(
  () => activeStep.value === ON_HOLD_STEPS.MAKE_COUNTER_OFFER_DETAILS,
)
const showSuspendReasons = computed(() =>
  [
    ON_HOLD_STEPS.TRANSPORT_ISSUE,
    ON_HOLD_STEPS.REFUSE_ITEM,
    ON_HOLD_STEPS.CUSTOMER_ACTION,
  ].includes(activeStep.value),
)

const buttonLabel = computed(() => {
  if (
    activeStep.value === ON_HOLD_STEPS.MAKE_COUNTER_OFFER_REASONS ||
    activeStep.value === ON_HOLD_STEPS.INTRO
  ) {
    return i18n(translations.nextButton)
  }

  if (activeStep.value === ON_HOLD_STEPS.MAKE_COUNTER_OFFER_DETAILS) {
    return i18n(translations.sendCounterOfferButton)
  }

  return i18n(translations.submitButton)
})

async function getCounterOfferReasonsGroups(step: string) {
  if (isEmpty(counterOfferReasonsGroups.value)) {
    isLoading.value = true

    try {
      const payload = await $httpFetch(
        getRefurbisherOrderCounterOffersReasons,
        {
          pathParams: {
            id: props.orderId,
          },
        },
      )
      counterOfferReasonsGroups.value = counterOfferReasonsGroupAdapter({
        groups: payload,
      })
      activeStep.value = step
    } catch (err) {
      openErrorToast()

      logger.error(
        '[BUYBACK][API][ORDER] Error getting counter offer reasons',
        {
          error: err as HttpApiError,
          owners: ['bot-squad-circularity-order-lifecycle-front'],
        },
      )
    }
    isLoading.value = false
  } else {
    activeStep.value = step
  }
}

async function getSuspendReasons(reason: string) {
  if (suspendReasons.value[reason]) {
    activeSuspendReasons.value = suspendReasons.value[reason]
    activeStep.value = reason
  } else {
    isLoading.value = true
    try {
      const payload = await $httpFetch(getRefurbisherSuspendReasons, {
        pathParams: {
          id: props.orderId,
          suspendCategory: reason,
        },
      })

      suspendReasons.value[reason] = payload
      activeSuspendReasons.value = suspendReasons.value[reason]
      activeStep.value = reason
    } catch (err) {
      emit('error')

      logger.error('[BUYBACK][API][ORDER] Error getting suspension reasons', {
        error: err as HttpApiError,
        owners: ['bot-squad-circularity-order-lifecycle-front'],
      })
    }
    isLoading.value = false
  }
}

const nextStep = async ({
  step,
  payload,
}: {
  step: string
  payload?: { reasons: string[] }
}) => {
  switch (step) {
    case ON_HOLD_STEPS.MAKE_COUNTER_OFFER_REASONS:
      await getCounterOfferReasonsGroups(step)
      break

    case ON_HOLD_STEPS.MAKE_COUNTER_OFFER_DETAILS:
      counterOfferReasonsGroups.value = counterOfferReasonsGroupAdapter({
        groups: counterOfferReasonsGroups.value,
        selected: payload?.reasons,
      })

      activeStep.value = step
      break

    case ON_HOLD_STEPS.CUSTOMER_ACTION:
    case ON_HOLD_STEPS.REFUSE_ITEM:
    case ON_HOLD_STEPS.TRANSPORT_ISSUE:
      await getSuspendReasons(step)
      break

    default:
  }
}

const setIntroStep = () => {
  activeStep.value = ON_HOLD_STEPS.INTRO
}

const handleSubmitSuspend = async (reasons: string[]) => {
  isLoading.value = true
  try {
    await $httpFetch(putRefurbisherOrderSuspension, {
      pathParams: {
        id: props.orderId,
      },
      body: {
        reasons,
      },
    })
    emit('success', {
      message: i18n(translations.suspendSuccessToastMessage),
      title: i18n(translations.suspendSuccessToastTitle),
    })
    closeModal(modalName.value)
  } catch (err) {
    emit('error')

    logger.error('[BUYBACK][API][ORDER] Error updating order suspension', {
      error: err as HttpApiError,
      owners: ['bot-squad-circularity-order-lifecycle-front'],
    })
  }
  isLoading.value = false
}

const setCounterOffer = async (payload: {
  counterOfferPrice: string
  comment: string
  reasons: string[]
  attachments: File[]
}) => {
  isLoading.value = true
  const body = new FormData()

  body.append('price', payload.counterOfferPrice)
  body.append('comment', payload.comment)
  payload.reasons.forEach((reason: string) => {
    body.append('reasons', reason)
  })
  payload.attachments.forEach((file: File) => {
    body.append('files', file)
  })

  try {
    await $httpFetch(putRefurbisherOrderCounterOffer, {
      pathParams: {
        id: props.orderId,
      },
      body,
    })
    emit('success', {
      message: i18n(translations.counterOfferSuccessToastMessage),
      title: i18n(translations.counterOfferSuccessToastTitle),
    })
    closeModal(modalName.value)
  } catch (err) {
    emit('error')

    logger.error('[BUYBACK][API][ORDER] Error updating counter offer', {
      error: err as HttpApiError,
      owners: ['bot-squad-circularity-order-lifecycle-front'],
    })
  }
  isLoading.value = false
}

const goToPreviousStep = () => {
  if (activeStep.value === ON_HOLD_STEPS.MAKE_COUNTER_OFFER_DETAILS) {
    activeStep.value = ON_HOLD_STEPS.MAKE_COUNTER_OFFER_REASONS
  } else {
    activeStep.value = ON_HOLD_STEPS.INTRO
  }
}
</script>
